import React, { useEffect, useState, useMemo } from "react";
import { api } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { toast } from "sonner";
import { Plus, Trash, PencilSimple, ClipboardText, FileText, Lock } from "@phosphor-icons/react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from "../../components/ui/dialog";
import AppointmentFilesDialog from "../../components/AppointmentFilesDialog";

const STATUSES = ["active", "pending", "terminated"];
const STATUS_COLORS = { active: "#34d399", pending: "#2563eb", terminated: "#fb7185" };

const BLANK = { agent_id: "", carrier_id: "", product_type: "", comp_level: 100, status: "active", effective_date: "", notes: "", writing_code: "" };

export default function AdminAppointments() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [rows, setRows] = useState([]);
  const [agents, setAgents] = useState([]);
  const [carriers, setCarriers] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(BLANK);
  const [filterAgent, setFilterAgent] = useState("");
  const [filterCarrier, setFilterCarrier] = useState("");
  const [filesFor, setFilesFor] = useState(null);

  const load = async () => {
    const [a, u, c] = await Promise.all([
      api.get("/appointments"),
      api.get("/users"),
      api.get("/carriers"),
    ]);
    setRows(a.data);
    setAgents(u.data.filter((x) => x.role === "agent"));
    setCarriers(c.data);
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    return rows.filter((r) =>
      (!filterAgent || r.agent_id === filterAgent) &&
      (!filterCarrier || r.carrier_id === filterCarrier)
    );
  }, [rows, filterAgent, filterCarrier]);

  const openNew = () => { setEditing(null); setForm(BLANK); setOpen(true); };
  const openEdit = (r) => {
    setEditing(r);
    setForm({
      agent_id: r.agent_id, carrier_id: r.carrier_id, product_type: r.product_type,
      comp_level: r.comp_level, status: r.status, effective_date: r.effective_date || "", notes: r.notes || "",
      writing_code: r.writing_code || "",
    });
    setOpen(true);
  };

  const save = async (e) => {
    e.preventDefault();
    const payload = { ...form, comp_level: Number(form.comp_level), effective_date: form.effective_date || null, notes: form.notes || null, writing_code: form.writing_code || null };
    try {
      if (editing) await api.patch(`/appointments/${editing.id}`, payload);
      else await api.post("/appointments", payload);
      toast.success(editing ? "Updated" : "Appointment added");
      setOpen(false);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const del = async (r) => {
    if (!window.confirm(`Remove ${r.agent_name}'s appointment with ${r.carrier_name}?`)) return;
    await api.delete(`/appointments/${r.id}`);
    toast.success("Removed");
    load();
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.25em] text-zinc-500">{isAdmin ? "Admin" : "Read-only"}</div>
          <h1 className="font-display font-black text-4xl md:text-5xl tracking-tighter mt-1 flex items-center gap-3">
            Appointments & Comp <ClipboardText weight="fill" className="text-[#2563eb]" />
          </h1>
          <p className="text-zinc-600 mt-2">
            {isAdmin
              ? "Who's appointed where, for which product, and at what compensation level."
              : "Your team's carrier appointments + compensation levels. Compensation is admin-managed and view-only here."}
          </p>
        </div>
        {isAdmin && (
          <Button onClick={openNew} className="rounded-none bg-[#09090b] text-white border-2 border-black hover:bg-black shadow-[4px_4px_0_0_#2563eb] font-bold uppercase tracking-widest text-xs h-11" data-testid="add-appt-btn">
            <Plus size={16} weight="bold" className="mr-1" /> New Appointment
          </Button>
        )}
        {!isAdmin && (
          <div className="inline-flex items-center gap-2 border-2 border-black px-3 h-11 bg-white text-zinc-600">
            <Lock size={14} weight="bold" />
            <span className="font-bold uppercase tracking-widest text-xs">Admin Edit Only</span>
          </div>
        )}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3">
        <select value={filterAgent} onChange={(e) => setFilterAgent(e.target.value)} className="h-11 border-2 border-black px-3 bg-white font-medium" data-testid="filter-agent">
          <option value="">All Agents</option>
          {agents.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
        </select>
        <select value={filterCarrier} onChange={(e) => setFilterCarrier(e.target.value)} className="h-11 border-2 border-black px-3 bg-white font-medium" data-testid="filter-carrier">
          <option value="">All Carriers</option>
          {carriers.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        <div className="brut-sm px-3 h-11 flex items-center font-bold text-sm bg-white">{filtered.length} records</div>
      </div>

      {/* Table */}
      <div className="brut bg-white overflow-x-auto">
        <table className="w-full border-collapse" data-testid="appointments-table">
          <thead>
            <tr className="bg-[#09090b] text-white">
              <th className="text-left px-4 py-3 font-bold uppercase text-xs tracking-widest">Agent</th>
              <th className="text-left px-4 py-3 font-bold uppercase text-xs tracking-widest">Carrier</th>
              <th className="text-left px-4 py-3 font-bold uppercase text-xs tracking-widest">Product</th>
              <th className="text-left px-4 py-3 font-bold uppercase text-xs tracking-widest">Writing Code</th>
              <th className="text-left px-4 py-3 font-bold uppercase text-xs tracking-widest">Status</th>
              <th className="text-right px-4 py-3 font-bold uppercase text-xs tracking-widest">Comp %</th>
              <th className="text-left px-4 py-3 font-bold uppercase text-xs tracking-widest">Effective</th>
              <th className="text-right px-4 py-3 font-bold uppercase text-xs tracking-widest">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((r) => (
              <tr key={r.id} className="border-b-2 border-black last:border-b-0 hover:bg-[#f4f4f5]">
                <td className="px-4 py-3 font-bold">{r.agent_name}</td>
                <td className="px-4 py-3">
                  <span className="inline-block border-2 border-black px-2 py-0.5 font-bold text-xs" style={{ background: r.carrier_color || "#2563eb" }}>
                    {r.carrier_name}
                  </span>
                </td>
                <td className="px-4 py-3">{r.product_type}</td>
                <td className="px-4 py-3 font-mono text-sm">{r.writing_code || <span className="text-zinc-400">—</span>}</td>
                <td className="px-4 py-3">
                  <span className="inline-block border-2 border-black px-2 py-0.5 text-xs font-bold uppercase tracking-widest" style={{ background: STATUS_COLORS[r.status] }}>
                    {r.status}
                  </span>
                </td>
                <td className="px-4 py-3 text-right font-display font-black text-lg tracking-tight">{r.comp_level}%</td>
                <td className="px-4 py-3 text-zinc-600">{r.effective_date || "—"}</td>
                <td className="px-4 py-3 text-right">
                  <div className="inline-flex gap-2">
                    <button onClick={() => setFilesFor(r)} className="border-2 border-black p-2 hover:bg-[#34d399]" data-testid={`files-appt-${r.id}`} title="Files">
                      <FileText size={14} weight="bold" />
                    </button>
                    {isAdmin && (
                      <>
                        <button onClick={() => openEdit(r)} className="border-2 border-black p-2 hover:bg-[#2563eb]" data-testid={`edit-appt-${r.id}`}><PencilSimple size={14} weight="bold" /></button>
                        <button onClick={() => del(r)} className="border-2 border-black p-2 hover:bg-[#fb7185]" data-testid={`delete-appt-${r.id}`}><Trash size={14} weight="bold" /></button>
                      </>
                    )}
                  </div>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr><td colSpan={8} className="px-4 py-10 text-center text-zinc-500">No appointments yet. Create one to start tracking comp levels.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="border-2 border-black rounded-none shadow-[6px_6px_0_0_#09090b] max-w-xl">
          <DialogHeader><DialogTitle className="font-display font-black tracking-tight">{editing ? "Edit Appointment" : "New Appointment"}</DialogTitle></DialogHeader>
          <form onSubmit={save} className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="font-bold uppercase text-xs tracking-widest">Agent</Label>
                <select value={form.agent_id} onChange={(e) => setForm({ ...form, agent_id: e.target.value })} required className="mt-1 w-full h-11 border-2 border-black px-3 bg-white font-medium" data-testid="appt-agent">
                  <option value="">Select…</option>
                  {agents.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
                </select>
              </div>
              <div>
                <Label className="font-bold uppercase text-xs tracking-widest">Carrier</Label>
                <select value={form.carrier_id} onChange={(e) => setForm({ ...form, carrier_id: e.target.value })} required className="mt-1 w-full h-11 border-2 border-black px-3 bg-white font-medium" data-testid="appt-carrier">
                  <option value="">Select…</option>
                  {carriers.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
              <div>
                <Label className="font-bold uppercase text-xs tracking-widest">Product</Label>
                <Input value={form.product_type} onChange={(e) => setForm({ ...form, product_type: e.target.value })} required placeholder="IUL / Annuity / Term" className="mt-1 border-2 border-black rounded-none h-11 focus-visible:ring-0" data-testid="appt-product" />
              </div>
              <div>
                <Label className="font-bold uppercase text-xs tracking-widest">Writing Code</Label>
                <Input value={form.writing_code} onChange={(e) => setForm({ ...form, writing_code: e.target.value })} placeholder="e.g. WC-12345" className="mt-1 border-2 border-black rounded-none h-11 focus-visible:ring-0 font-mono" data-testid="appt-writing-code" />
              </div>
              <div>
                <Label className="font-bold uppercase text-xs tracking-widest">Comp %</Label>
                <Input type="number" step="0.01" value={form.comp_level} onChange={(e) => setForm({ ...form, comp_level: e.target.value })} required className="mt-1 border-2 border-black rounded-none h-11 focus-visible:ring-0" data-testid="appt-comp" />
              </div>
              <div>
                <Label className="font-bold uppercase text-xs tracking-widest">Status</Label>
                <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className="mt-1 w-full h-11 border-2 border-black px-3 bg-white font-medium">
                  {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div>
                <Label className="font-bold uppercase text-xs tracking-widest">Effective Date</Label>
                <Input type="date" value={form.effective_date} onChange={(e) => setForm({ ...form, effective_date: e.target.value })} className="mt-1 border-2 border-black rounded-none h-11 focus-visible:ring-0" />
              </div>
            </div>
            <div>
              <Label className="font-bold uppercase text-xs tracking-widest">Notes</Label>
              <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} className="mt-1 border-2 border-black rounded-none focus-visible:ring-0" />
            </div>
            <Button type="submit" className="w-full h-11 rounded-none bg-[#2563eb] text-white border-2 border-black shadow-[4px_4px_0_0_#09090b] hover:shadow-[6px_6px_0_0_#09090b] hover:-translate-y-0.5 font-bold uppercase tracking-widest" data-testid="save-appt-btn">
              Save
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      <AppointmentFilesDialog
        appointment={filesFor}
        open={!!filesFor}
        onClose={() => setFilesFor(null)}
        canEdit={isAdmin}
      />
    </div>
  );
}
